// WRITING BACK TO users/{uid} WITHOUT ERASING WHAT IS ALREADY THERE.
//
// Both intake screens save the member's details back to his profile after a claim, with
// `setDoc(..., { merge: true })`. Merge protects fields you DO NOT SEND. It does not protect a field
// you send as an empty string - that writes the empty string.
//
// On 2026-08-23 that turned into a live break. QuickFile let a member reach step 4 with no craft
// set, then filed and wrote `craft: ''` over a perfectly good profile. Craft is the first key in
// COMMITTEE_LOOKUP, so with it blank no general committee resolves, with no committee no agreement
// resolves, and rule analysis refuses to run - on THIS claim and on every later one, because the
// blank is now the stored value. A defect that repairs itself never gets found; this one seeded
// itself and got worse.
//
// WHY ONLY FOUR FIELDS ARE PROTECTED. Blanking must stay possible in general: a member who deletes
// his phone number is entitled to have it deleted, and a helper that quietly refused would be its
// own bug. So only the identity and routing keys are protected - the ones that are DERIVED from a
// dropdown the member cannot type into, where an empty value never means "clear this", it only ever
// means "this screen did not know". Everything else passes through untouched and stays clearable.
export const NEVER_BLANK = ['craft', 'railroad', 'workLocation', 'committee'];

/**
 * Strip protected keys that carry no value, so a merge cannot erase them.
 * @param {object} patch what the caller wants to write to users/{uid}
 * @returns {object} the same patch with blank protected keys removed
 */
// PLAN R-5 / finding L-5 (2026-09-20). THE ONE TEST FOR "IS THE FILER THE CLAIMANT".
//
// Both intake screens write the CLAIMANT's details into the SIGNED-IN user's profile document.
// When a Local Chairperson files for a member that overwrites the officer's own name, roster
// number and seniority date with the member's. The write is only ever correct when the person
// filing is the person the claim is about.
//
// Ruling R-4 already settled how that is decided - the e-mail on the form is the signed-in
// account's - and wrote it inside the claim payload builder. Writing the same test a second time
// in the screens is the duplicated-authority defect of directives section XI, which on this
// project has produced six others. It lives here, once, and every caller reads it.
//
// Identity is never inferred from a name: two members share a name, and a wrong match would copy
// one man's record over another's.
export function filerIsClaimant({ filerEmail, claimantEmail }) {
  const a = String(filerEmail || '').trim().toLowerCase();
  const b = String(claimantEmail || '').trim().toLowerCase();
  return !!a && !!b && a === b;
}

export function profilePatch(patch) {
  const out = {};
  for (const [k, v] of Object.entries(patch || {})) {
    if (NEVER_BLANK.includes(k)) {
      const s = typeof v === 'string' ? v.trim() : v;
      if (s === '' || s === null || s === undefined) continue;   // never write a blank over one of these
    }
    out[k] = v;
  }
  return out;
}

// Which keys this patch would have erased. Not used to decide anything - used so a caller can log
// it, and so the test can assert on the reason rather than only on the result.
export function blankedProtectedKeys(patch) {
  return Object.keys(patch || {}).filter(k => {
    if (!NEVER_BLANK.includes(k)) return false;
    const v = patch[k];
    const s = typeof v === 'string' ? v.trim() : v;
    return s === '' || s === null || s === undefined;
  });
}
